/**
 * Elevation Sampler — fetches terrain-RGB (Terrarium-encoded) DEM tiles and
 * samples elevation at arbitrary lat/lng positions along a route.
 *
 * Uses an in-memory tile cache so that repeated queries along the same route
 * or nearby area don't re-fetch tiles.
 *
 * Terrarium encoding:  elevation_m = (R * 256 + G + B / 256) − 32768
 */

import { getAuthHeader } from './AuthContext';

export interface ElevationSample {
  /** Cumulative distance from route start in NM */
  distanceNm: number;
  /** Elevation in feet MSL */
  elevationFt: number;
  /** Latitude of sample point */
  latitude: number;
  /** Longitude of sample point */
  longitude: number;
}

export interface RoutePoint {
  lat: number;
  lng: number;
  label?: string;
}

const TILE_SIZE = 256;
const METERS_TO_FEET = 3.28084;
const DEG_TO_RAD = Math.PI / 180;
const NM_PER_RADIAN = 3440.065;

// ─── Tile math ───────────────────────────────────────────────────────────────

function lngToTileX(lng: number, zoom: number): number {
  return Math.floor(((lng + 180) / 360) * (1 << zoom));
}

function latToTileY(lat: number, zoom: number): number {
  const latRad = lat * DEG_TO_RAD;
  return Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * (1 << zoom));
}

function lngToPixelX(lng: number, zoom: number, tileX: number): number {
  const worldX = ((lng + 180) / 360) * (1 << zoom) * TILE_SIZE;
  return Math.floor(worldX - tileX * TILE_SIZE);
}

function latToPixelY(lat: number, zoom: number, tileY: number): number {
  const latRad = lat * DEG_TO_RAD;
  const worldY = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * (1 << zoom) * TILE_SIZE;
  return Math.floor(worldY - tileY * TILE_SIZE);
}

function terrariumToMeters(r: number, g: number, b: number): number {
  return (r * 256 + g + b / 256) - 32768;
}

// ─── Haversine ───────────────────────────────────────────────────────────────

function haversineNm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const dLat = (lat2 - lat1) * DEG_TO_RAD;
  const dLng = (lng2 - lng1) * DEG_TO_RAD;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLng / 2) ** 2;
  return 2 * Math.asin(Math.sqrt(a)) * NM_PER_RADIAN;
}

/** Interpolate a position along the route polyline at fraction t ∈ [0,1] */
function interpolateRoute(
  route: RoutePoint[],
  t: number,
  totalNm: number,
): { lat: number; lng: number } {
  const targetDist = t * totalNm;
  let cumDist = 0;
  for (let i = 1; i < route.length; i++) {
    const legDist = haversineNm(route[i - 1].lat, route[i - 1].lng, route[i].lat, route[i].lng);
    if (cumDist + legDist >= targetDist) {
      const frac = legDist > 0 ? (targetDist - cumDist) / legDist : 0;
      return {
        lat: route[i - 1].lat + (route[i].lat - route[i - 1].lat) * frac,
        lng: route[i - 1].lng + (route[i].lng - route[i - 1].lng) * frac,
      };
    }
    cumDist += legDist;
  }
  return { lat: route[route.length - 1].lat, lng: route[route.length - 1].lng };
}

// ─── ElevationSampler ────────────────────────────────────────────────────────

export class ElevationSampler {
  private tileUrl: string;
  private zoom: number;
  private tileCache = new Map<string, ImageData>();
  private pendingTiles = new Map<string, Promise<ImageData | null>>();

  /**
   * @param tileUrl  XYZ URL template with {z}/{x}/{y} (Terrarium-encoded DEM)
   * @param zoom     Zoom level to sample at (10-12 is good for aviation)
   */
  constructor(tileUrl: string, zoom = 11) {
    this.tileUrl = tileUrl;
    this.zoom = zoom;
  }

  /** Fetch a DEM tile and return its ImageData */
  private async fetchTile(tileX: number, tileY: number): Promise<ImageData | null> {
    const key = `${this.zoom}/${tileX}/${tileY}`;
    if (this.tileCache.has(key)) return this.tileCache.get(key)!;
    if (this.pendingTiles.has(key)) return this.pendingTiles.get(key)!;

    const url = this.tileUrl
      .replace('{z}', String(this.zoom))
      .replace('{x}', String(tileX))
      .replace('{y}', String(tileY));

    const promise = (async (): Promise<ImageData | null> => {
      try {
        const res = await fetch(url, { headers: getAuthHeader() });
        if (!res.ok) return null;
        const blob = await res.blob();
        const bitmap = await createImageBitmap(blob);
        const canvas = new OffscreenCanvas(TILE_SIZE, TILE_SIZE);
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(bitmap, 0, 0);
        const imageData = ctx.getImageData(0, 0, TILE_SIZE, TILE_SIZE);
        this.tileCache.set(key, imageData);
        return imageData;
      } catch {
        return null;
      } finally {
        this.pendingTiles.delete(key);
      }
    })();

    this.pendingTiles.set(key, promise);
    return promise;
  }

  /** Get elevation at a single point in feet MSL. Returns null if tile unavailable. */
  async getElevation(lat: number, lng: number): Promise<number | null> {
    const tileX = lngToTileX(lng, this.zoom);
    const tileY = latToTileY(lat, this.zoom);
    const imageData = await this.fetchTile(tileX, tileY);
    if (!imageData) return null;

    let px = lngToPixelX(lng, this.zoom, tileX);
    let py = latToPixelY(lat, this.zoom, tileY);
    px = Math.max(0, Math.min(TILE_SIZE - 1, px));
    py = Math.max(0, Math.min(TILE_SIZE - 1, py));

    const idx = (py * TILE_SIZE + px) * 4;
    const r = imageData.data[idx];
    const g = imageData.data[idx + 1];
    const b = imageData.data[idx + 2];
    return terrariumToMeters(r, g, b) * METERS_TO_FEET;
  }

  /**
   * Sample elevation along a route at evenly-spaced intervals.
   *
   * @param route      Array of waypoints
   * @param numSamples Number of elevation samples (default 150)
   * @returns          Array of ElevationSample with real DEM-derived elevations
   */
  async sampleRoute(route: RoutePoint[], numSamples = 150): Promise<ElevationSample[]> {
    if (route.length < 2) return [];

    // Compute total route distance
    let totalNm = 0;
    for (let i = 1; i < route.length; i++) {
      totalNm += haversineNm(route[i - 1].lat, route[i - 1].lng, route[i].lat, route[i].lng);
    }
    if (totalNm <= 0) return [];

    // Pre-fetch all unique tiles we'll need (batch for speed)
    const tileKeys = new Set<string>();
    for (let i = 0; i <= numSamples; i++) {
      const t = i / numSamples;
      const pt = interpolateRoute(route, t, totalNm);
      const tx = lngToTileX(pt.lng, this.zoom);
      const ty = latToTileY(pt.lat, this.zoom);
      tileKeys.add(`${tx}/${ty}`);
    }

    // Fetch all needed tiles in parallel
    await Promise.all(
      [...tileKeys].map((key) => {
        const [tx, ty] = key.split('/').map(Number);
        return this.fetchTile(tx, ty);
      }),
    );

    // Now sample each point (tiles are all cached, so this is sync-fast)
    const samples: ElevationSample[] = [];
    for (let i = 0; i <= numSamples; i++) {
      const t = i / numSamples;
      const pt = interpolateRoute(route, t, totalNm);
      const elevFt = await this.getElevation(pt.lat, pt.lng);
      samples.push({
        distanceNm: t * totalNm,
        elevationFt: Math.max(0, elevFt ?? 0),
        latitude: pt.lat,
        longitude: pt.lng,
      });
    }

    return samples;
  }

  /** Clear the in-memory tile cache */
  clearCache(): void {
    this.tileCache.clear();
  }

  /** Compute route stats from a set of samples */
  static computeStats(samples: ElevationSample[]): {
    minFt: number;
    maxFt: number;
    avgFt: number;
    totalClimbFt: number;
    totalDescentFt: number;
    totalDistNm: number;
  } {
    if (samples.length === 0) return { minFt: 0, maxFt: 0, avgFt: 0, totalClimbFt: 0, totalDescentFt: 0, totalDistNm: 0 };
    let min = Infinity, max = -Infinity, sum = 0, climb = 0, descent = 0;
    for (let i = 0; i < samples.length; i++) {
      const e = samples[i].elevationFt;
      if (e < min) min = e;
      if (e > max) max = e;
      sum += e;
      if (i > 0) {
        const delta = e - samples[i - 1].elevationFt;
        if (delta > 0) climb += delta;
        else descent += Math.abs(delta);
      }
    }
    return {
      minFt: min,
      maxFt: max,
      avgFt: sum / samples.length,
      totalClimbFt: climb,
      totalDescentFt: descent,
      totalDistNm: samples[samples.length - 1]?.distanceNm ?? 0,
    };
  }
}
